import React, { memo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts';

export interface CloseButtonProps {
  /** Callback when button is clicked */
  onClick: () => void;
  /** Position variant - affects placement on screen */
  position?: 'fixed' | 'absolute';
  /** Custom CSS classes */
  className?: string;
  /** Custom aria-label (defaults to translated "Close") */
  ariaLabel?: string;
  /** Whether to show text label alongside icon */
  showLabel?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
}

/**
 * CloseButton - A standardized close/back button component with iOS-safe touch targets
 * 
 * Designed for iOS accessibility with:
 * - Minimum 44x44px touch target (Apple HIG recommendation)
 * - Safe area inset support for notched devices
 * - RTL-aware positioning
 * - Consistent styling across the app
 * 
 * @example
 * // Fixed position in print view
 * <CloseButton onClick={handleClose} position="fixed" showLabel />
 * 
 * @example
 * // Absolute position in modal
 * <CloseButton onClick={handleClose} position="absolute" />
 */
export const CloseButton: React.FC<CloseButtonProps> = memo(({
  onClick,
  position = 'absolute',
  className = '',
  ariaLabel,
  showLabel = false,
  size = 'medium',
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  const label = ariaLabel || t('buttons.close', 'Close');
  
  // Size configurations with iOS-safe minimum touch targets
  const sizeClasses = {
    small: {
      button: 'w-11 h-11 sm:w-12 sm:h-12', // 44px minimum, 48px on sm
      icon: 16,
      text: 'text-sm',
    },
    medium: {
      button: 'w-11 h-11 sm:w-12 sm:h-12', // 44px minimum, 48px on sm
      icon: 18,
      text: 'text-base',
    },
    large: {
      button: 'w-12 h-12 sm:w-14 sm:h-14', // 48px minimum, 56px on sm
      icon: 20,
      text: 'text-lg',
    },
  };
  
  const config = sizeClasses[size];
  
  // Position classes with safe area support
  const positionClasses = position === 'fixed'
    ? `fixed top-4 ${isRTL ? 'left-4' : 'right-4'} z-[101]`
    : `absolute top-2 ${isRTL ? 'left-2' : 'right-2'} sm:top-3 sm:${isRTL ? 'left-3' : 'right-3'}`;
  
  // Add safe-area padding for fixed position buttons (iOS notch support)
  const style = position === 'fixed' 
    ? {
        top: `max(1rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))`,
        [isRTL ? 'left' : 'right']: `max(1rem, calc(env(safe-area-inset-${isRTL ? 'left' : 'right'}, 0px) + 0.5rem))`,
      } as React.CSSProperties
    : undefined;
  
  if (showLabel) {
    // Button with icon and text label
    return (
      <button
        type="button"
        onClick={onClick}
        className={`
          ${positionClasses}
          inline-flex items-center justify-center gap-2
          bg-purple-400 dark:bg-purple-500
          text-white
          px-4 py-2.5
          rounded-full
          shadow-lg hover:shadow-xl
          hover:bg-purple-300 dark:hover:bg-purple-400
          active:scale-95
          transition-all duration-150
          font-heebo font-bold
          focus:outline-none focus:ring-4 focus:ring-purple-300 dark:focus:ring-purple-500/50
          min-h-[44px]
          ${config.text}
          ${className}
        `}
        aria-label={label}
        style={style}
      >
        <X size={config.icon} className={isRTL ? 'ml-1' : 'mr-1'} />
        {label}
      </button>
    );
  }
  
  // Icon-only button (circular)
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        ${positionClasses}
        ${config.button}
        rounded-full
        bg-gray-100 dark:bg-dark-surface
        hover:bg-gray-200 dark:hover:bg-dark-border
        flex items-center justify-center
        transition-colors
        text-gray-500 dark:text-dark-muted
        hover:text-gray-700 dark:hover:text-dark-text
        shadow-md hover:shadow-lg
        active:scale-95
        focus:outline-none focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-500/50
        ${className}
      `}
      aria-label={label}
      style={style}
    >
      <X size={config.icon} />
    </button>
  );
});

CloseButton.displayName = 'CloseButton';
